'use client';
import React, { ReactElement, useEffect, useState } from 'react';
import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import { BiCheck } from 'react-icons/bi';
import { RiSparkling2Line } from 'react-icons/ri';
import { HiOutlineSparkles } from 'react-icons/hi2';
import LouiseButton from '@/components/buttons/LouiseButton';
import ReactHtmlParser from 'react-html-parser';

type Props = {
  badge: string;
  title: string;
  description: string;
  subtitle: string;
  features: Features[];
  badgeCatchEye: BadgeCatchEye;
  footerSectionBlock?: FooterSectionBlock[];
  locale: SupportedLanguages;
};

type Features = {
  title: string;
  description: string;
  subtitle: string;
  img: string;
  icon: ReactElement;
};

type BadgeCatchEye = {
  title: string;
  description?: string;
};

type FooterSectionBlock = {
  metric: string;
  metricLabel: string;
};

const ChatbotsNewFeatures = (props: Props) => {
  const { t } = useTranslation(props.locale);

  const features = props.features.map((feature: Features) => {
    return {
      title: feature.title,
      description: feature.description,
      subtitle: feature.subtitle,
      img: feature.img,
      icon: feature.icon,
    };
  });

  const footerSectionBlock = props.footerSectionBlock?.map((footerSection: FooterSectionBlock) => {
    return {
      metric: footerSection.metric,
      metricLabel: footerSection.metricLabel,
    };
  });

  // Default icons if not provided in props

  const [selectIndex, setSelectIndex] = useState(0);
  const [isUserSelected, setIsUserSelected] = useState(false);

  useEffect(() => {
    if (isUserSelected || features.length === 0) return; // Stop auto-rotation if user selects manually or no features

    const interval = setInterval(() => {
      setSelectIndex((prevIndex) => (prevIndex + 1) % features.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isUserSelected, features.length]);

  const handleFeatureClick = (index: number) => {
    setSelectIndex(index);
    setIsUserSelected(true);

    setTimeout(() => setIsUserSelected(false), 10000); // Resume auto-rotation after 10 seconds
  };

  return (
    <section className="tw-py-10 lg:tw-py-10 tw-bg-transparent tw-font-['Lexend']">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Header */}
        <div className="tw-text-center tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <RiSparkling2Line className="tw-w-4 tw-h-4" />
            {props.badge}
          </div>
          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
            {ReactHtmlParser(props.title)}
          </h2>
          <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-leading-relaxed">
            {ReactHtmlParser(props.description)}
          </p>
        </div>

        <div className="tw-grid lg:tw-grid-cols-2 tw-gap-16 tw-items-center">
          {/* Features Selection */}
          <div className="tw-space-y-6">
            <div className="tw-space-y-4">
              {features.map((feature, index) => {
                const isSelected = selectIndex === index;

                return (
                  <div
                    onClick={() => handleFeatureClick(index)}
                    key={index}
                    className={`tw-group tw-cursor-pointer tw-p-6 tw-rounded-2xl tw-border-2 tw-transition-all tw-duration-300 tw-transform hover:tw-scale-[1.02] ${
                      isSelected
                        ? 'tw-border-blue-500 tw-bg-blue-50 tw-shadow-lg'
                        : 'tw-border-gray-200 tw-bg-white hover:tw-border-blue-300 tw-shadow-sm hover:tw-shadow-md'
                    }`}
                  >
                    <div className="tw-flex tw-items-start tw-gap-4">
                      {/* Icon */}
                      <div
                        className={`tw-flex-shrink-0 tw-w-12 tw-h-12 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 ${
                          isSelected
                            ? 'tw-bg-blue-600 tw-text-white'
                            : 'tw-bg-gray-100 tw-text-gray-600 group-hover:tw-bg-blue-100 group-hover:tw-text-blue-600'
                        }`}
                      >
                        {feature.icon}
                      </div>

                      {/* Content */}
                      <div className="tw-flex-grow tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                          <h3
                            className={`tw-text-lg tw-font-bold tw-transition-colors tw-duration-300 ${
                              isSelected ? 'tw-text-blue-900' : 'tw-text-gray-900'
                            }`}
                          >
                            {feature.title}
                          </h3>

                          {/* Selection Indicator */}
                          <div
                            className={`tw-flex-shrink-0 tw-transition-all tw-duration-300 ${
                              isSelected ? 'tw-opacity-100' : 'tw-opacity-0 group-hover:tw-opacity-50'
                            }`}
                          >
                            <BiCheck
                              className={`tw-w-6 tw-h-6 ${isSelected ? 'tw-text-blue-600' : 'tw-text-gray-400'}`}
                            />
                          </div>
                        </div>

                        <p
                          className={`tw-text-sm tw-leading-relaxed tw-transition-colors tw-duration-300 ${
                            isSelected ? 'tw-text-blue-700' : 'tw-text-gray-600'
                          }`}
                        >
                          {feature.description}
                        </p>

                        {/* Call to action hint */}
                        {isSelected && (
                          <p
                            className={`tw-text-sm tw-pt-2 tw-leading-relaxed tw-transition-colors tw-duration-300 ${
                              isSelected ? 'tw-text-blue-700' : 'tw-text-gray-600'
                            }`}
                          >
                            {ReactHtmlParser(feature.subtitle)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Progress bar for selected item */}
                    {isSelected && !isUserSelected && (
                      <div className="tw-mt-4 tw-w-full tw-h-1 tw-bg-blue-200 tw-rounded-full tw-overflow-hidden">
                        <div
                          className="tw-h-full tw-bg-blue-600 tw-rounded-full tw-transition-all tw-duration-[5000ms] tw-ease-linear"
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Action buttons */}
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-pt-6 tw-border-t tw-border-gray-200">
              <LouiseButton
                color="blue"
                cta={t('ChatBotsNewFeatures/try-now')}
                ctaTarget="register"
                locale={props.locale}
                gradiant={true}
              />
            </div>
          </div>

          {/* Image Display */}
          <div className="tw-relative">
            {/* Background decoration */}
            <div className="tw-hidden lg:tw-block tw-absolute tw--top-4 tw--right-4 tw-w-full tw-h-full tw-bg-gradient-to-br tw-from-blue-100 tw-to-purple-100 tw-rounded-3xl tw-opacity-30"></div>

            {/* Main image container */}
            <div className="tw-relative tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-2xl tw-border tw-border-gray-200">
              <div className="tw-relative tw-overflow-hidden tw-rounded-2xl">
                <Image
                  src={features[selectIndex]?.img || '/placeholder.jpg'}
                  alt={`${features[selectIndex]?.title} demo`}
                  width={600}
                  height={400}
                  className="tw-w-full tw-h-auto tw-transition-all tw-duration-500 tw-transform"
                  priority
                  quality={100}
                />
              </div>

              {/* Feature indicators */}
              <div className="tw-absolute tw--bottom-2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-flex tw-gap-2">
                {features.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleFeatureClick(index)}
                    className={`tw-w-3 tw-h-3 tw-rounded-full tw-transition-all tw-duration-300 ${
                      index === selectIndex ? 'tw-bg-blue-600 tw-scale-125' : 'tw-bg-gray-300 hover:tw-bg-gray-400'
                    }`}
                    aria-label={`View ${features[index]?.title}`}
                  />
                ))}
              </div>
            </div>

            {/* Desktop floating card - hidden on mobile */}
            <div className="tw-hidden md:tw-block tw-absolute tw-bottom-5 tw--right-6 tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600 tw-text-white tw-rounded-2xl tw-p-4 tw-shadow-lg">
              <div className="tw-flex tw-items-center tw-gap-2">
                <HiOutlineSparkles className="tw-w-5 tw-h-5" />
                <div>
                  <div className="tw-text-sm tw-font-bold">{props.badgeCatchEye.title}</div>
                  <div className="tw-text-xs tw-opacity-90">{props.badgeCatchEye.description}</div>
                </div>
              </div>
            </div>

            {/* Mobile metrics - stack below image */}
            <div className="tw-block md:tw-hidden tw-mt-6 tw-space-y-4">
              <div className="tw-bg-gradient-to-r tw-from-green-500 tw-to-emerald-600 tw-text-white tw-rounded-2xl tw-p-4 tw-shadow-lg">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <HiOutlineSparkles className="tw-w-5 tw-h-5" />
                  <div>
                    <div className="tw-text-sm tw-font-bold">{props.badgeCatchEye.title}</div>
                    <div className="tw-text-xs tw-opacity-90">{props.badgeCatchEye.description}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats */}
        {footerSectionBlock && (
          <div className="tw-grid grid-cols-2 md:tw-grid-cols-4 tw-gap-8 tw-mt-20 tw-pt-16 tw-border-t tw-border-gray-200">
            {footerSectionBlock.map((stat, index) => (
              <div
                key={index}
                className="tw-text-center tw-p-4 tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100"
              >
                <div className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">{stat.metric}</div>
                <div className="tw-text-sm tw-text-gray-600">{ReactHtmlParser(stat.metricLabel)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ChatbotsNewFeatures;
